<template>
  <el-dialog
    :close-on-click-modal="false"
    close-on-press-escape
    :model-value="visible"
    @close="$emit('close')"
    :title="form.id ? 'Редактировать клиента' : 'Добавить клиента'"
    width="60%"
    destroy-on-close>

    <template #header>
      <div class="row">
        <div class="col-8 p-2 ps-4 text-start fw-bold">
          <span>{{ form.fio }}</span>
        </div>
        <div class="col-4 p-2">
          <span v-if="form.balance !== undefined"
                :class="['client-balance', { negative: form.balance < 0, positive: form.balance > 0 }]">
          Баланс: <b>{{ Number(form.balance).toLocaleString() }} ₽</b>
        </span>
        </div>
        <el-divider />
      </div>
    </template>

    <el-tabs v-model="activeTab">
      <el-tab-pane name="client" class="p-3">
        <template #label>
          <el-icon><UserFilled /></el-icon>
          <span>Клиент</span>
        </template>

        <el-form
          :model="form"
          ref="formRef"
          label-width="120px"
          label-position="top"
          size="large">

          <!-- Верхний блок с аватаром -->
          <div class="client-header">
            <div class="client-header__avatar">
              <el-avatar
                class="avatar"
                :src="avatarSrc"
                :size="100"
                shape="circle"
                @click="onAvatarClick">
                {{ avatarInitials }}
              </el-avatar>
            </div>
            <div style="flex:1;">
              <el-row :gutter="12">
                <el-col :span="12">
                  <el-form-item label="ФИО" prop="fio" required>
                    <el-input v-model="form.fio" autocomplete="off" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="Дата рождения" prop="birth_date">
                    <el-date-picker v-model="form.birth_date" type="date" value-format="YYYY-MM-DD" />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="Пол" prop="sex">
                <el-radio-group v-model="form.sex">
                  <el-radio-button :label="1">Муж</el-radio-button>
                  <el-radio-button :label="2">Жен</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </div>
          </div>

          <!-- Контактные данные -->
          <el-divider>Контактная информация</el-divider>
          <el-row :gutter="12">
            <el-col :span="8">
              <el-form-item label="Телефон" prop="phone" :rules="[{ validator: atLeastOneContact, trigger: 'blur' }]">
                <vue-tel-input
                  v-model="form.phone"
                  @validate="handlePhoneValidation"
                  :format-on-display="true"
                  :mode="'international'"
                  v-bind="bindProps"
                  :preferred-countries="['ru', 'by', 'kz']"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="Электронная почта" prop="email" :rules="[{ validator: atLeastOneContact, trigger: 'blur' }]">
                <el-input v-model="form.email" type="email" placeholder="Введите email" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="Telegram ID / Имя пользователя" prop="telegram_user_id" :rules="[{ validator: atLeastOneContact, trigger: 'blur' }]">
                <el-input v-model="form.telegram_user_id" placeholder="Введите Telegram ID или имя пользователя" />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Адрес -->
          <el-divider>Адрес и локация</el-divider>
          <el-row :gutter="12">
            <el-col :span="12">
              <el-form-item label="Город" prop="city_id">
                <el-select v-model="form.city_id" filterable placeholder="Город">
                  <el-option v-for="city in cities" :key="city.id" :label="city.name" :value="city.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="Адрес" prop="address">
                <el-input v-model="form.address" />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Дополнительные данные -->
          <el-divider>Дополнительно</el-divider>

          <el-row :gutter="12">
            <el-col :span="12">
              <el-form-item label="ИНН" prop="inn">
                <el-input v-model="form.inn" maxlength="24" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="Документ" prop="document">
                <el-input v-model="form.document" maxlength="64" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="Комментарий" prop="comment">
            <el-input v-model="form.comment" type="textarea" :rows="3" placeholder="Комментарий о клиенте..." />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane name="appointments" :disabled="!form.id">
        <template #label>
          <el-icon>
            <Memo />
          </el-icon>
          <span>Посещения</span>
        </template>
        <!-- Тут будет компонент/таблица посещений клиента -->
        <div style="padding: 24px; color: var(--ui-color-text-muted);">В разработке...</div>
      </el-tab-pane>

      <el-tab-pane name="subscriptions" :disabled="!form.id">
        <template #label>
          <el-icon>
            <Ticket />
          </el-icon>
          <span>Абонементы</span>
        </template>
        <!-- Тут будет компонент/таблица абонементов клиента -->
        <div style="padding: 24px; color: var(--ui-color-text-muted);">
          <ClientAbonements
            v-if="form.id"
            :client-id="form.id"
            @abonement-updated="fetchClientData"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane name="operations" :disabled="!form.id">
        <template #label>
          <el-icon>
            <Wallet />
          </el-icon>
          <span>Операции</span>
        </template>
        <!-- Тут будет компонент/таблица операций клиента -->
        <div style="padding: 24px; color: var(--ui-color-text-muted);">
          <ClientPayments v-if="form.id" :client-id="form.id" @abonement-updated="fetchClientData"/>
        </div>
      </el-tab-pane>
    </el-tabs>
    <template #footer>
      <el-button @click="$emit('close')">Отмена</el-button>
      <el-button
        type="primary"
        @click="onSave"
        :loading="saving"
        :disabled="saving">
        {{ form.id ? 'Обновить' : 'Создать' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Memo, Ticket, UserFilled, Wallet } from '@element-plus/icons-vue'
import { mapActions, mapState } from 'vuex'
import ClientAbonements from '@/components/util/clients/ClientAbonements.vue'
import ClientPayments from '@/components/util/clients/ClientPayments.vue'

export default {
  name: 'ClientForm',
  components: {
    ClientPayments,
    ClientAbonements,
    UserFilled, Memo, Ticket, Wallet
  },
  props: {
    visible: Boolean,
    model: Object,
    cities: Array
  },
  data() {
    return {
      saving: false,
      bindProps: {
        inputOptions: {
          placeholder: 'Введите номер'
        }
      },
      customFieldValues: {},
      form: { ...this.model },
      rules: {
        fio: [{ required: true, message: 'Укажите ФИО', trigger: 'blur' }],
        phone: [{ required: true, message: 'Телефон обязателен', trigger: 'blur' }],
        // city_id: [{ required: true, message: 'Выберите город', trigger: 'change' }], // город не обязателен
        email: [
          { type: 'email', message: 'Введите корректный email', trigger: ['blur', 'change'] }
        ]
      },
      activeTab: 'client',
      duplicateClient: null,
      duplicateEmail: null,
      checkingDuplicate: false,
      initialized: false,
      initialPhone: '',
      loadingInit: false
    }
  },
  watch: {
    model: {
      handler(val) {
        this.form = { ...val }
        this.initialPhone = val.phone ?? ''
        this.initialized = true
        this.customFieldValues = {}
        if (val.custom_fields) {
          for (const [id, value] of Object.entries(val.custom_fields)) {
            const field = this.customFields.find(f => f.id === id)
            if (field && field.type === 'number') {
              // Чётко в number (null, если пусто)
              this.customFieldValues[id] =
                value !== null && value !== undefined && value !== '' ? Number(value) : null
            } else if (field && (field.type === 'date' || field.type === 'datetime')) {
              this.customFieldValues[id] = (typeof value === 'string' && value.length ? value : null)
            } else {
              this.customFieldValues[id] = value
            }
          }
        }
        this.isPhoneComplete = false
        this.loadingInit = true
        this.$nextTick(() => {
          // Надёжное обновление vue-tel-input: сбрасываем и возвращаем номер
          const oldPhone = this.form.phone
          this.form.phone = ''
          this.$nextTick(() => {
            this.form.phone = oldPhone
            this.loadingInit = false
          })
        })
      },
      deep: true,
      immediate: true
    },
    customFields: {
      handler() {
        this.rules = {
          fio: [{ required: true, message: 'Укажите ФИО', trigger: 'blur' }],
          phone: [{ required: true, message: 'Телефон обязателен', trigger: 'blur' }],
          // city_id: [{ required: true, message: 'Выберите город', trigger: 'change' }], // город не обязателен
          email: [
            { type: 'email', message: 'Введите корректный email', trigger: ['blur', 'change'] }
          ],
          ...this.customRules
        }
      },
      immediate: true
    },
    'form.phone': {
      async handler(newVal) {
        if (!this.initialized || this.loadingInit || newVal === this.initialPhone) return
        const cleaned = typeof newVal === 'string' ? newVal.replace(/\D/g, '') : ''
        if (cleaned.length >= 5) {
          this.checkingDuplicate = true
          try {
            await this.searchClients({ query: cleaned })
            const found = (this.search_clients || []).find(c => {
              const clientPhone = (c.phone || '').replace(/\D/g, '')
              return clientPhone.endsWith(cleaned) && c.id !== this.form.id
            })
            this.duplicateClient = found || null
          } catch (e) {
            console.error(e)
          } finally {
            this.checkingDuplicate = false
          }
        } else {
          this.duplicateClient = null
        }
      }
    },
    'form.email': {
      async handler(newVal) {
        if (!this.initialized || this.loadingInit) return
        // Простая валидация email
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!newVal || !emailPattern.test(newVal.trim())) {
          this.duplicateEmail = null
          return
        }
        const query = newVal.trim().toLowerCase()
        try {
          await this.searchClients({ query })
          const found = (this.search_clients || []).find(c =>
            (c.email || '').toLowerCase() === query && c.id !== this.form.id
          )
          this.duplicateEmail = found || null
        } catch (e) {
          console.error(e)
        }
      }
    }
  },
  computed: {
    ...mapState('clients', ['search_clients']),
    ...mapState('custom_fields', ['fields']),
    ...mapState('certificates', ['certificates']),
    ...mapState('abonements', ['abonements']),
    customFields() {
      return this.fields // Уже отфильтровано на этапе запроса
    },
    customRules() {
      const rules = {}
      for (const field of this.customFields) {
        if (field.required && field.type === 'number') {
          rules[`custom_${field.id}`] = [
            { required: true, message: 'Обязательное поле', trigger: 'change' },
            {
              validator: (rule, value, callback) => {
                if (typeof value !== 'number' || isNaN(value)) {
                  callback(new Error('Обязательное поле'))
                } else {
                  callback()
                }
              }, trigger: 'change'
            }
          ]
        } else if (field.required) {
          rules[`custom_${field.id}`] = [{ required: true, message: 'Обязательное поле', trigger: 'blur' }]
        }
      }
      return rules
    },
    avatarSrc() {
      if (this.form.avatar) return this.form.avatar
      if (this.form.sex === 1) return '/img/avatar-male.svg'
      if (this.form.sex === 2) return '/img/avatar-female.svg'
      if (this.form.fio) return null
      return '/img/avatar-male.svg'
    },
    avatarInitials() {
      if (!this.form.fio) return ''
      const parts = this.form.fio.trim().split(/\s+/)
      const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('')
      return initials || ''
    },
    fullForm() {
      const combined = { ...this.form }
      for (const [id, value] of Object.entries(this.customFieldValues)) {
        combined[`custom_${id}`] = value
      }
      return combined
    }
  },
  methods: {
    ...mapActions('clients', ['searchClients']),
    ...mapActions('payments', ['fetchPayments']),
    onAvatarClick() {
      console.log('avatar click')
    },
    atLeastOneContact(rule, value, callback) {
      const phone = (this.form?.phone || '').trim()
      const email = (this.form?.email || '').trim()
      const tg = (this.form?.telegram_user_id || '').trim()
      if (phone || email || tg) return callback()
      callback(new Error('Укажите хотя бы один способ связи'))
    },
    async searchClientsFn(query, cb) {
      if (!query || query.length < 5) return cb([])
      await this.searchClients({ query })
      const results = (this.clients || []).map(c => ({
        ...c,
        value: c.fio + (c.phone ? ` (${c.phone})` : '')
      }))
      cb(results)
    },
    getFieldComponent(field) {
      switch (field.type) {
        case 'text':
          return 'el-input'
        case 'textarea':
          return 'el-input'
        case 'number':
          return 'el-input-number'
        case 'date':
          return 'el-date-picker'
        case 'datetime':
          return 'el-date-picker'
        case 'select':
          return 'el-select'
        case 'multiselect':
          return 'el-select'
        case 'radio':
          return 'el-radio-group'
        case 'checkbox':
          return 'el-checkbox'
        case 'switch':
          return 'el-switch'
        case 'color':
          return 'el-color-picker'
        case 'slider':
          return 'el-slider'
        default:
          return 'el-input'
      }
    },
    getFieldProps(field) {
      const ph = field.placeholder || field.label
      switch (field.type) {
        case 'textarea':
          return { type: 'textarea', rows: 2, placeholder: ph }
        case 'number':
          return { min: field.min ?? 0, max: field.max ?? 99999, step: 1, placeholder: ph }
        case 'date':
          return { type: 'date', 'value-format': 'YYYY-MM-DD', placeholder: ph }
        case 'datetime':
          return { type: 'datetime', 'value-format': 'YYYY-MM-DD HH:mm', placeholder: ph }
        case 'select':
          return { placeholder: ph, clearable: true }
        case 'multiselect':
          return { multiple: true, placeholder: ph, clearable: true }
        case 'radio':
          return {}
        case 'checkbox':
          return { label: field.label }
        case 'switch':
          return {}
        case 'color':
          return { showAlpha: true }
        case 'slider':
          return { min: field.min ?? 0, max: field.max ?? 100 }
        default:
          return { placeholder: ph }
      }
    },
    handlePhoneValidation(isValid) {
      this.isPhoneComplete = isValid.valid
    },
    async onSave() {
      if (this.saving) return
      this.saving = true

      try {
        Object.entries(this.customFieldValues).forEach(([id, val]) => {
          const field = this.customFields.find(f => f.id === id)
          if (field && field.type === 'number' && typeof val !== 'number' && val !== null && val !== '') {
            this.customFieldValues[id] = Number(val)
          }
        })

        if (this.duplicateClient) {
          ElMessage.error('Клиент с таким номером уже существует: ' + this.duplicateClient.fio)
          this.saving = false
          return
        }
        if (this.duplicateEmail) {
          ElMessage.error('Клиент с таким email уже существует: ' + this.duplicateEmail.fio)
          this.saving = false
          return
        }

        this.$refs.formRef.validate(async valid => {
          if (!valid) {
            this.saving = false
            return
          }

          if (this.form.phone && !this.isPhoneComplete) {
            ElMessage.error('Пожалуйста, введите полный номер телефона')
            this.saving = false
            return
          }

          const formWithCustom = {
            ...this.form,
            custom_fields: { ...this.customFieldValues }
          }

          try {
            const savedClient = await new Promise(resolve => {
              this.$emit('save', formWithCustom, resolve)
            })

            if (savedClient && savedClient.id) {
              this.form = { ...savedClient }
              this.initialPhone = savedClient.phone ?? ''
              this.activeTab = 'client'
              ElMessage.success('Клиент сохранён')
              this.$nextTick(() => {
                this.form.id = savedClient.id
              })
            }
          } catch (e) {
            console.error(e)
            ElMessage.error('Ошибка при сохранении клиента')
          } finally {
            this.saving = false
          }
        })
      } catch (err) {
        console.error(err)
        this.saving = false
      }
    },
    async fetchClientData() {
      try {
        const response = await this.$store.dispatch('clients/getClientById', { id: this.form.id })
        if (response && response.client) {
          this.form = { ...response.client }
          ElMessage.success('Данные клиента обновлены')
        }
      } catch (error) {
        console.error('Ошибка при обновлении клиента:', error)
        ElMessage.error('Не удалось обновить данные клиента')
      }
      await this.fetchPayments({ client_id: this.form.id });
    }
  }
}
</script>
<style scoped>
.avatar {
  font-size: 28px;
}

.client-balance {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-regular);
}

.client-balance.positive {
  color: var(--el-color-success);
}

.client-balance.negative {
  color: var(--el-color-danger);
}
</style>
<style>
.vue-tel-input .vti__dropdown {
  z-index: 3000 !important; /* выше чем у el-dialog, el-select и т.д. */
}

.vue-tel-input {
  height: 38px !important;
  border: none !important;
  width: 100% !important;
}

.vue-tel-input-block {
  height: 40px !important;
  justify-content: flex-start;
  padding: 2px;
  z-index: 99999999;
}

.header {
  text-align: start;
  font-weight: 600;
}

.el-tabs__item .el-icon {
  font-size: 18px;
  vertical-align: middle;
  margin-right: 4px;
}

.client-header {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.client-header__avatar {
  cursor: pointer;
}
</style>
